import {announce} from '@github-ui/aria-live'
import {useSortable} from '@github-ui/drag-and-drop'
import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import {testIdProps} from '@github-ui/test-id-props'
import {type ContainerBreakpointFunction, useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {VisuallyHidden} from '@github-ui/visually-hidden'
import {ArrowDownIcon, ArrowUpIcon, GrabberIcon, KebabHorizontalIcon, PencilIcon, XIcon} from '@primer/octicons-react'
import type {SxProp} from '@primer/react'
import {ActionList, ActionMenu, Box, Button, Flash, FormControl, IconButton, TextInput} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {clsx} from 'clsx'
import {useRef, useState} from 'react'

import type {NewSingleOption, PersistedOption} from '../../../api/columns/contracts/single-select'
import {useDragAndDrop} from '../../../helpers/dnd-kit/drag-and-drop-context'
import {replaceShortCodesWithEmojis} from '../../../helpers/emojis'
import {getInitialState} from '../../../helpers/initial-state'
import {emptySingleSelectOption, type SettingsOption} from '../../../helpers/new-column'
import {useEmojiAutocomplete} from '../../../hooks/common/use-emoji-autocomplete'
import {useReturnFocus} from '../../../hooks/common/use-return-focus'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {DragAndDropResources, Resources} from '../../../strings'
import {SanitizedHtml} from '../../dom/sanitized-html'
import {SingleSelectOptionModal} from './single-select-option-modal'
import {ReorderSingleSelectItemsModal} from './single-select-reorder-modal'
import {SingleSelectToken} from './single-select-token'

interface SingleSelectFormProps extends SxProp {
  options: Array<SettingsOption>
  canRemoveOption: boolean
  onAddOption: (option: NewSingleOption) => void
  onUpdateOption: (updatedOption: SettingsOption) => void
  onRemoveOption: (id: string) => void
}

export const SingleSelectForm = ({
  options,
  onAddOption: external_onAddOption,
  onUpdateOption,
  onRemoveOption,
  canRemoveOption,
  sx,
}: SingleSelectFormProps) => {
  const {
    projectLimits: {singleSelectColumnOptionsLimit},
  } = getInitialState()

  const dropRef = useRef<HTMLOListElement>(null)
  const optionsLimitReached = options.length >= singleSelectColumnOptionsLimit

  const selectInputByIndex = (index: number) =>
    dropRef.current?.querySelector<HTMLInputElement>(`input[data-option-index='${index}']`)

  const onAddOption = (option: NewSingleOption = emptySingleSelectOption) => {
    external_onAddOption(option)
    setTimeout(() => selectInputByIndex(options.length)?.focus())
  }

  const [container, containerRef] = useState<HTMLDivElement | null>(null)
  const breakpoint = useContainerBreakpoint(container)

  return (
    <Box
      sx={{
        borderStyle: 'solid',
        borderWidth: breakpoint([0, 1]),
        borderColor: 'border.default',
        borderRadius: 2,
        ...sx,
      }}
      ref={containerRef}
    >
      <Box as="ol" ref={dropRef} sx={{listStyle: 'none', m: 0, p: 0}}>
        {options.slice(0, singleSelectColumnOptionsLimit).map((option, index) => (
          <SingleSelectItem
            key={option.id}
            option={option}
            options={options}
            index={index}
            enableOptionRemoval={canRemoveOption}
            onRemove={() => onRemoveOption(option.id)}
            onChange={onUpdateOption}
            breakpoint={breakpoint}
          />
        ))}
      </Box>

      <Box sx={{p: breakpoint([0, 3]), pt: breakpoint([2, 3])}}>
        {optionsLimitReached ? (
          <Flash variant="warning" sx={{p: 2}}>
            {Resources.singleSelectOptionLimitWarning(singleSelectColumnOptionsLimit)}
          </Flash>
        ) : (
          <NewItemCreator onCreate={onAddOption} />
        )}
      </Box>
    </Box>
  )
}

interface NewItemCreatorProps {
  onCreate: (option?: NewSingleOption) => void
}

const NewItemCreator = ({onCreate}: NewItemCreatorProps) => {
  const [name, setName] = useState('')

  const isValid = name.trim() !== ''

  const onSubmit = () => {
    if (isValid) {
      onCreate({...emptySingleSelectOption, name: replaceShortCodesWithEmojis(name)})
      setName('')
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onSubmit()
    }
  }
  const inputProps = useIgnoreKeyboardActionsWhileComposing(onKeyDown)
  const autocompleteProps = useEmojiAutocomplete()

  return (
    <Box as="fieldset" sx={{display: 'flex', gap: 2, border: 0, padding: 0, margin: 0}} onSubmit={onSubmit}>
      <VisuallyHidden as="legend">Add option</VisuallyHidden>
      <FormControl sx={{flex: 1}}>
        <FormControl.Label visuallyHidden>Label text</FormControl.Label>
        <InlineAutocomplete {...autocompleteProps} sx={{width: '100%'}}>
          <TextInput
            block
            value={name}
            onChange={ev => setName(ev.target.value)}
            placeholder="Add option…"
            autoComplete="false"
            {...inputProps}
          />
        </InlineAutocomplete>
      </FormControl>
      <Button onClick={onSubmit} disabled={!isValid}>
        Add
      </Button>
    </Box>
  )
}

interface SingleSelectItemProps {
  option: PersistedOption | SettingsOption
  options: Array<PersistedOption | SettingsOption>
  index: number
  enableOptionRemoval: boolean
  onRemove: React.MouseEventHandler
  onChange: (updatedOption: SettingsOption) => void
  breakpoint: ContainerBreakpointFunction
}

const boxWithDropSashStyle: BetterSystemStyleObject = {
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.default',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  /**
   * Adding sash, a thick blue vertical bar displayed on top or bottom to indicate
   * where a dragged item will land.
   */
  position: 'relative',
  '&::after,  &::before': {
    content: '""',
    display: 'none',
    position: 'absolute',
    left: 0,
    right: 0,
    bg: 'accent.emphasis',
    height: '4px',
    border: 0,
    borderRadius: '6px',
    zIndex: 1,
  },
  'body.is-dragging &.show-sash-after': {
    '&::after': {
      display: 'block',
      bottom: 0,
      transform: 'translateY(50%)',
    },
  },
  'body.is-dragging &.show-sash-before': {
    '&::before': {
      display: 'block',
      top: 0,
      transform: 'translateY(-50%)',
    },
  },
}

function SingleSelectItem({
  index,
  option,
  options,
  enableOptionRemoval,
  onRemove,
  onChange,
  breakpoint,
}: SingleSelectItemProps) {
  const {setNodeRef, setActivatorNodeRef, listeners, isDragging} = useSortable({
    id: option.id,
    data: {metadata: {id: option.id}},
  })

  const {onMouseDown, onClick, onKeyDown} = listeners as {
    onMouseDown: React.MouseEventHandler
    onClick: React.MouseEventHandler
    onKeyDown: React.KeyboardEventHandler
  }

  const {getDropSide, moveToPosition} = useDragAndDrop()
  const prefersReducedMotion = usePrefersReducedMotion()

  const dropSide = getDropSide(option.id)
  const className = clsx({
    [`show-sash-${dropSide}`]: dropSide,
  })

  const {memex_single_select_dropdown} = useEnabledFeatures()

  const [editModalVisible, setEditModalVisible] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [reorderModalVisible, setReorderModalVisable] = useState(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)

  useReturnFocus(editModalVisible, editButtonRef)
  useReturnFocus(reorderModalVisible, editButtonRef)

  return (
    <Box
      key={option.id}
      as="li"
      ref={setNodeRef}
      sx={{
        px: breakpoint([0, 3]),
        py: breakpoint([1, 3]),
        borderBottomWidth: breakpoint([0, 1]),
        ...boxWithDropSashStyle,
      }}
      className={className}
      role="listitem"
      {...testIdProps('singleSelectItem')}
    >
      <Box sx={{display: 'flex', position: 'relative'}}>
        <Box onMouseDown={onMouseDown} sx={{position: 'absolute', width: '100%', height: '100%', cursor: 'grab'}} />
        <IconButton
          sx={{p: 1, color: 'fg.muted', cursor: 'grab', mr: 1}}
          aria-label={`Move ${option.name}`}
          ref={setActivatorNodeRef}
          {...testIdProps('singleSelectDragHandle')}
          onKeyDown={onKeyDown}
          onClick={onClick}
          variant="invisible"
          role={isDragging ? 'application' : 'button'}
          icon={GrabberIcon}
        />
      </Box>
      <SingleSelectToken option={option} />
      <SanitizedHtml
        sx={{
          flex: 1,
          px: 2,
          color: 'fg.subtle',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          m: 0,
        }}
        as="p"
      >
        {'descriptionHtml' in option ? option.descriptionHtml : option.description}
      </SanitizedHtml>

      {!memex_single_select_dropdown && (
        <>
          <IconButton
            icon={PencilIcon}
            variant="invisible"
            size="small"
            onClick={() => setEditModalVisible(true)}
            sx={{color: 'fg.muted'}}
            aria-label={`Open edit dialog for ${option.name}`}
            ref={editButtonRef}
          />
          {enableOptionRemoval && (
            <IconButton
              icon={XIcon}
              variant="invisible"
              size="small"
              onClick={onRemove}
              sx={{color: 'fg.muted'}}
              aria-label={`Remove ${option.name}`}
            />
          )}
        </>
      )}

      {memex_single_select_dropdown && (
        <ActionMenu onOpenChange={setMenuOpen} open={menuOpen} anchorRef={editButtonRef}>
          <ActionMenu.Anchor>
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              aria-label={`Open field actions for ${option.name}`}
              size="small"
              sx={{color: 'fg.muted'}}
              {...testIdProps('single-select-item-menu-button')}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item
                onSelect={() => setEditModalVisible(true)}
                aria-label={`Open edit dialog for ${option.name}`}
                {...testIdProps('single-select-item-edit-button')}
              >
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit option
              </ActionList.Item>
              <ActionList.Item
                aria-label={`Open move dialog for ${option.name}`}
                onSelect={() => setReorderModalVisable(true)}
                {...testIdProps('single-select-item-move-button')}
              >
                <ActionList.LeadingVisual>
                  <GrabberIcon />
                </ActionList.LeadingVisual>
                Move
              </ActionList.Item>
              {index !== 0 && (
                <ActionList.Item
                  aria-label={`Move ${option.name} up`}
                  onSelect={() => {
                    moveToPosition(index, index - 1, true)
                    if (index === 1) {
                      announce(DragAndDropResources.moveToTop(option.name))
                    } else {
                      announce(DragAndDropResources.moveBefore(option.name, options[index - 1]?.name ?? ''))
                    }
                  }}
                  {...testIdProps('singleSelectItemMoveUpButton')}
                >
                  <ActionList.LeadingVisual>
                    <ArrowUpIcon />
                  </ActionList.LeadingVisual>
                  Move up
                </ActionList.Item>
              )}
              {index !== options.length - 1 && (
                <ActionList.Item
                  aria-label={`Move ${option.name} down`}
                  onSelect={() => {
                    moveToPosition(index, index + 1, false)
                    if (index === options.length - 2) {
                      announce(DragAndDropResources.moveToBottom(option.name))
                    } else {
                      announce(DragAndDropResources.moveAfter(option.name, options[index + 1]?.name ?? ''))
                    }
                  }}
                  {...testIdProps('singleSelectItemMoveDownButton')}
                >
                  <ActionList.LeadingVisual>
                    <ArrowDownIcon />
                  </ActionList.LeadingVisual>
                  Move down
                </ActionList.Item>
              )}
              {enableOptionRemoval && (
                <>
                  <ActionList.Divider />
                  <ActionList.Item
                    variant="danger"
                    onClick={onRemove}
                    aria-label={`Remove ${option.name}`}
                    {...testIdProps('single-select-item-delete-button')}
                  >
                    <ActionList.LeadingVisual>
                      <XIcon />
                    </ActionList.LeadingVisual>
                    Remove option
                  </ActionList.Item>
                </>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}

      {editModalVisible && (
        <SingleSelectOptionModal
          initialOption={option}
          onCancel={() => setEditModalVisible(false)}
          onSave={updatedOption => {
            setEditModalVisible(false)
            onChange({...option, ...updatedOption})
          }}
        />
      )}
      {memex_single_select_dropdown && reorderModalVisible && (
        <ReorderSingleSelectItemsModal
          title={option}
          options={options}
          currentPosition={index}
          onCancel={() => {
            setReorderModalVisable(false)
            announce(DragAndDropResources.cancelMove(option.name))
          }}
          onSave={(currentPosition, newPosition, before) => {
            setReorderModalVisable(false)
            moveToPosition(currentPosition, newPosition, before)
            setTimeout(() => {
              editButtonRef.current?.scrollIntoView({
                behavior: prefersReducedMotion ? 'instant' : 'smooth',
                block: 'center',
              })
            }, 1)
          }}
        />
      )}
    </Box>
  )
}

try{ SingleSelectForm.displayName ||= 'SingleSelectForm' } catch {}
try{ NewItemCreator.displayName ||= 'NewItemCreator' } catch {}
try{ SingleSelectItem.displayName ||= 'SingleSelectItem' } catch {}